import { cn } from '@/lib/utils';
import { useTenant } from '@/providers/TenantProvider';

/*
 * Marca na interface.
 *
 * Duas situacoes diferentes moram aqui:
 *
 * 1. Area de membros de um cliente — quem manda e a marca DELE, que chega em
 *    runtime pelo TenantProvider. E o caso normal do produto.
 * 2. Chrome da plataforma (login antes de resolver o tenant, admin da Xtreed,
 *    pagina de erro) — ai aparece a Xtreed.
 *
 * O manual de identidade proibe expressamente o logotipo sem o simbolo, entao
 * a marca Xtreed so e desenhada como conjunto: `XtreedMark` sempre traz os
 * dois. Quando so cabe um quadrado, use `XtreedSymbol`, que e a variante 04
 * (simbolo isolado) prevista no manual.
 */

const SIZES = {
  sm: { icon: 28, text: 'text-lg', lockup: 96 },
  md: { icon: 36, text: 'text-xl', lockup: 124 },
  lg: { icon: 44, text: 'text-2xl', lockup: 152 },
  xl: { icon: 52, text: 'text-3xl', lockup: 180 },
} as const;

type Size = keyof typeof SIZES;

/** Simbolo isolado (variante 04). Sempre no verde da marca. */
export function XtreedSymbol({
  size = 36,
  className,
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      viewBox="256.90 253.20 566.20 573.60"
      width={size}
      height={size}
      role="img"
      aria-label="Xtreed"
      className={cn('text-xtreed-green', className)}
      fill="currentColor"
    >
      <path d="m823.1,430.32l-70.7-70.71-95.43,95.43c-24.66,24.66-24.66,64.65,0,89.31l93.94,93.93,70.7-70.71-67.88-67.88,69.37-69.37Z" />
      <path d="m329.1,359.6l-70.7,70.71,67.88,67.88-69.38,69.38,70.71,70.71,95.43-95.43c24.66-24.66,24.66-64.65,0-89.31l-93.94-93.94Z" />
      <path d="m588.57,584.28v-92.16c0-23.72,9.42-46.47,26.2-63.24l102.94-102.94-70.71-70.7-107.39,107.39-109.46-109.46-70.71,70.7,102.94,102.94c16.77,16.77,26.2,39.52,26.2,63.24v91.53c0,40.13-15.94,78.62-44.32,107l-2.7,2.7c-22.77,22.77-53.65,35.56-85.84,35.56h-3.54v99.99h37.73c36.82,0,72.13-14.63,98.17-40.66l49.15-49.15,49.15,49.15c26.04,26.04,69.85,40.66,106.67,40.66h42.87v-99.99h-17.17c-32.2,0-63.08-12.79-85.84-35.56h0c-28.38-28.38-44.32-66.87-44.32-107Z" />
    </svg>
  );
}

/**
 * Lockup completo: simbolo + logotipo.
 *
 * O simbolo fica no verde da marca e o logotipo herda `currentColor`, entao a
 * mesma marcacao serve fundo claro e escuro — que e exatamente o que o manual
 * chama de variante negativa, sem precisar de um segundo arquivo.
 */
export function XtreedMark({
  width = 124,
  className,
}: {
  width?: number;
  className?: string;
}) {
  return (
    <svg
      viewBox="181.50 367.50 1512.00 375.20"
      width={width}
      role="img"
      aria-label="Xtreed"
      className={cn('text-foreground', className)}
    >
      <g className="fill-xtreed-green">
        <path d="m551.77,483.38l-46.24-46.24-62.41,62.41c-16.13,16.13-16.13,42.28,0,58.41l61.43,61.43,46.24-46.24-44.39-44.39,45.37-45.37Z" />
        <path d="m228.7,437.14l-46.24,46.24,44.39,44.39-45.37,45.37,46.24,46.24,62.41-62.41c16.13-16.13,16.13-42.28,0-58.41l-61.43-61.43Z" />
        <path d="m398.39,584.07v-60.27c0-15.51,6.16-30.39,17.13-41.36l67.32-67.32-46.24-46.24-70.23,70.23-71.58-71.58-46.24,46.24,67.32,67.32c10.97,10.97,17.13,25.85,17.13,41.36v59.86c0,26.25-10.43,51.42-28.98,69.98l-1.76,1.76c-14.89,14.89-35.08,23.25-56.14,23.25h-2.31v65.39h24.68c24.08,0,47.17-9.57,64.2-26.59l32.14-32.14,32.14,32.14c17.03,17.03,45.68,26.59,69.76,26.59h28.03v-65.39h-11.23c-21.06,0-41.25-8.36-56.14-23.25h0c-18.56-18.56-28.99-43.73-28.99-69.98Z" />
      </g>
      <g fill="currentColor">
        <path d="m1003.42,540.7h46.05v31.44h.73c8.77-19.74,24.85-34.72,53-34.72,4.39,0,7.68.36,10.23,1.1v42.03c-2.92-.73-8.41-1.46-14.99-1.46-27.78,0-47.15,18.64-47.15,47.15v93.57h-47.88v-179.1Z" />
        <path d="m1119.28,630.25c0-52.27,32.89-94.3,91.01-94.3s89.55,42.03,86.99,105.27h-130.12c1.46,28.88,16.08,48.61,43.49,48.61,21.57,0,33.99-12.42,38.01-25.95h46.79c-9.14,33.99-38.75,60.67-84.8,60.67-58.85,0-91.38-41.3-91.38-94.3Zm130.12-16.81c-2.56-27.05-15.35-43.13-39.84-43.13s-38.38,16.08-42.03,43.13h81.87Z" />
        <path d="m1314.82,630.25c0-52.27,32.89-94.3,91.01-94.3s89.55,42.03,86.99,105.27h-130.12c1.46,28.88,16.08,48.61,43.49,48.61,21.57,0,33.99-12.42,38.01-25.95h46.79c-9.14,33.99-38.75,60.67-84.8,60.67-58.85,0-91.38-41.3-91.38-94.3Zm130.12-16.81c-2.56-27.05-15.35-43.13-39.84-43.13s-38.38,16.08-42.03,43.13h81.87Z" />
        <path d="m1510.37,629.89c0-53,32.16-93.94,80.41-93.94,27.78,0,47.15,15.35,53.73,29.24h1.1v-101.25h47.88v255.86h-46.05v-27.05h-1.1c-6.21,13.16-24.12,31.8-55.19,31.8-49.34,0-80.78-40.57-80.78-94.67Zm88.91,54.1c25.21,0,38.81-20.9,38.81-53.73s-13.93-53.73-39.14-53.73-39.14,20.57-39.14,53.73,14.59,53.73,39.47,53.73Z" />
        <path d="m958.47,685.08c-9.14,0-16.45-5.12-16.45-19.01v-74.2l-32.88-17.91h67.97v-33.26h-35.09v-53.73h-47.15v53.73h-23.39v33.26h23.39v101.25c0,36.18,23.76,49.34,51.17,49.34,13.16,0,27.41-2.93,32.17-4.39v-36.55c-3.29.73-12.06,1.83-19.74,1.46Z" />
        <path d="m791.12,587.5l78.58-123.56h-59.59l-168.12,255.85h61.02l55.21-91.72h1.1l54.07,91.72h61.4l-83.67-132.29Z" />
        <polygon points="757.82 543.52 698.83 543.52 645.27 463.94 705.57 463.94 757.82 543.52" />
      </g>
    </svg>
  );
}

interface XtreedLogoProps {
  className?: string;
  size?: Size;
  showText?: boolean;
}

export function XtreedLogo({
  className,
  size = 'md',
  showText = true,
}: XtreedLogoProps) {
  const { icon, text, lockup } = SIZES[size];
  const tenant = useTenant();

  // Sem logo de cliente, quem assina a tela e a Xtreed — e ai vale o lockup
  // oficial, nao o simbolo com o nome ao lado em fonte de UI.
  if (!tenant?.logo) {
    // Sem espaco para o nome (menu recolhido), cai na variante 04 do manual.
    // Nao da para so estreitar o lockup: ele e 4:1, entao na largura do icone
    // sobrariam 9px de altura e o logotipo viraria borrao.
    return showText ? (
      <XtreedMark width={lockup} className={className} />
    ) : (
      <XtreedSymbol size={icon} className={className} />
    );
  }

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div
        className="overflow-hidden rounded-[22%]"
        style={{ width: icon, height: icon }}
      >
        <img
          src={tenant.logo}
          alt={tenant.name}
          width={icon}
          height={icon}
          className="block h-full w-full object-contain"
        />
      </div>

      {showText && (
        <span
          className={cn(
            'font-display font-bold tracking-tight',
            text,
            'text-foreground',
          )}
        >
          {tenant.name}
        </span>
      )}
    </div>
  );
}
